import { Link } from 'react-router-dom'

import { StatusPill } from '@/components/ui/StatusPill'
import { HiringFunnel, RecentHires } from '@/features/admin/components/AdminDashboardInsights'
import {
  RecruiterInsightTiles,
  TeamActivity,
  TimeToHireChart,
} from '@/features/recruiter/components/RecruiterDashboardInsights'
import { useRecruiterDashboardQuery } from '@/features/recruiter/hooks/useRecruiterDashboard'
import { useAuthStore } from '@/store/authStore'
import { formatDate, humanizeEnum } from '@/utils/format'

interface RecentJob {
  id: number
  title: string
  status: string
  applicantCount: number
  createdAt: string
}

const STATUS_CONFIG: Record<string, { variant: string; icon: string }> = {
  published: { variant: 'success', icon: 'ti-circle-check' },
  draft:     { variant: 'warning', icon: 'ti-pencil' },
  paused:    { variant: 'secondary', icon: 'ti-player-pause' },
  closed:    { variant: 'danger', icon: 'ti-circle-x' },
  archived:  { variant: 'info', icon: 'ti-archive' },
}


function StatusProgressBar({ status, count, total }: { status: string; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0
  const cfg = STATUS_CONFIG[status] ?? { variant: 'secondary', icon: 'ti-dots' }
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="fs-13 fw-medium d-flex align-items-center gap-1">
          <i className={`ti ${cfg.icon} text-${cfg.variant}`} />
          {humanizeEnum(status)}
        </span>
        <span className="fs-13 text-muted">
          {count} <span className="fs-12">({pct}%)</span>
        </span>
      </div>
      <div className="progress" style={{ height: 6 }}>
        <div
          className={`progress-bar bg-${cfg.variant}`}
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  )
}

export function RecruiterDashboardWidgets() {
  const { data, isPending, isError } = useRecruiterDashboardQuery()
  const user = useAuthStore((s) => s.user)

  if (isPending) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5 text-muted fs-13">
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        Loading dashboard…
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <div className="text-center">
          <i className="ti ti-alert-circle fs-36 text-danger mb-2 d-block" />
          <p className="fs-14 fw-semibold mb-1">Failed to load dashboard</p>
          <p className="fs-13 text-muted">Please refresh the page to try again.</p>
        </div>
      </div>
    )
  }

  const publishedCount = data.countsByStatus['published'] ?? 0
  const draftCount = data.countsByStatus['draft'] ?? 0
  const totalStatusCount = Object.values(data.countsByStatus).reduce((a, b) => a + b, 0)
  const pausedCount = data.countsByStatus['paused'] ?? 0

  return (
    <>
      {/* Welcome banner */}
      <div className="card custom-card mb-3 mt-4" style={{ background: 'linear-gradient(135deg, var(--primary-color) 0%, #7c6ef5 100%)', border: 'none' }}>
        <div className="card-body py-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h5 className="fw-semibold mb-1 text-white">
                Welcome back{user?.name ? `, ${user.name}` : ''}!
              </h5>
              <p className="mb-0 fs-13" style={{ color: 'rgba(255,255,255,0.8)' }}>
                Here's a snapshot of your job postings and candidate applications.
              </p>
            </div>
            <Link to="/recruiter/jobs/new" className="btn btn-light btn-sm fw-semibold">
              <i className="ti ti-plus me-1" />
              Post New Job
            </Link>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="row g-3 mb-3">
        {/* Tile 1: Total Jobs with Published / Draft / Paused bifurcation */}
        <div className="col-sm-6 col-xl-3">
          <div className="card custom-card">
            <div className="card-body d-flex align-items-center justify-content-between py-3">
              <div>
                <p className="text-muted fs-13 mb-1">Total Jobs</p>
                <h4 className="fw-semibold mb-0">{data.totalJobs}</h4>
                <p className="fs-12 text-muted mb-0 mt-1">
                  <span className="text-success fw-medium">{publishedCount} pub</span>
                  {' · '}
                  <span className="text-warning fw-medium">{draftCount} draft</span>
                  {pausedCount > 0 && <><span className="text-muted"> · </span><span className="text-secondary fw-medium">{pausedCount} paused</span></>}
                </p>
              </div>
              <span className="avatar avatar-lg avatar-rounded bg-primary">
                <i className="ti ti-briefcase fs-18 text-white" />
              </span>
            </div>
          </div>
        </div>

        {/* Tile 2: Applications Received */}
        <div className="col-sm-6 col-xl-3">
          <div className="card custom-card">
            <div className="card-body d-flex align-items-center justify-content-between py-3">
              <div>
                <p className="text-muted fs-13 mb-1">Applications Received</p>
                <h4 className="fw-semibold mb-0">{data.totalApplications}</h4>
                <p className="fs-12 text-muted mb-0 mt-1">Across all jobs</p>
              </div>
              <span className="avatar avatar-lg avatar-rounded bg-info">
                <i className="ti ti-file-text fs-18 text-white" />
              </span>
            </div>
          </div>
        </div>

        {/* Tile 3: Total Candidates — same set as /recruiter/all-candidates */}
        <div className="col-sm-6 col-xl-3">
          <Link
            to="/recruiter/all-candidates"
            className="card custom-card text-decoration-none text-reset"
            aria-label={`View all ${data.totalCandidatesCount} candidates`}
          >
            <div className="card-body d-flex align-items-center justify-content-between py-3">
              <div>
                <p className="text-muted fs-13 mb-1">Total Candidates</p>
                <h4 className="fw-semibold mb-0">{data.totalCandidatesCount}</h4>
                <p className="fs-12 text-primary mb-0 mt-1">
                  View all candidates <i className="ti ti-arrow-right" />
                </p>
              </div>
              <span className="avatar avatar-lg avatar-rounded bg-success">
                <i className="ti ti-users fs-18 text-white" />
              </span>
            </div>
          </Link>
        </div>

        {/* Tile 4: Total Hired */}
        <div className="col-sm-6 col-xl-3">
          <Link
            to="/recruiter/hires"
            className="card custom-card text-decoration-none text-reset"
            aria-label={`View all ${data.hiredCount} hired candidates`}
          >
            <div className="card-body d-flex align-items-center justify-content-between py-3">
              <div>
                <p className="text-muted fs-13 mb-1">Total Hired</p>
                <h4 className="fw-semibold mb-0">{data.hiredCount}</h4>
                <p className="fs-12 text-primary mb-0 mt-1">
                  View hired candidates <i className="ti ti-arrow-right" />
                </p>
              </div>
              <span className="avatar avatar-lg avatar-rounded bg-warning">
                <i className="ti ti-trophy fs-18 text-white" />
              </span>
            </div>
          </Link>
        </div>
      </div>

      <RecruiterInsightTiles data={data} />

      {/* Hiring funnel + recent hires — this organization's jobs only */}
      <div className="row g-3 mb-3">
        <div className="col-xl-6">
          <HiringFunnel stages={data.hiringFunnel} subtitle="Applications to your jobs that reached each stage" />
        </div>
        <div className="col-xl-6">
          <RecentHires hires={data.recentHires} viewAllTo="/recruiter/hires" showOrganization={false} />
        </div>
      </div>

      {/* Team activity (organization admin only) + time to hire */}
      <div className="row g-3 mb-3">
        {data.teamActivity && (
          <div className="col-xl-7">
            <TeamActivity rows={data.teamActivity} />
          </div>
        )}
        <div className={data.teamActivity ? 'col-xl-5' : 'col-12'}>
          <TimeToHireChart timeToHire={data.timeToHire} />
        </div>
      </div>

      {/* Recent jobs + Status breakdown */}
      <div className="row g-3">
        <div className="col-xl-8">
          <div className="card custom-card h-100">
            <div className="card-header">
              <div className="card-title fs-14 fw-semibold">
                <i className="ti ti-clock-hour-4 me-2 text-primary" />
                Recent Jobs
              </div>
              <Link to="/recruiter/jobs" className="btn btn-primary btn-sm">
                View All
              </Link>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th className="fs-13">Job Title</th>
                    <th className="fs-13">Status</th>
                    <th className="fs-13 text-center">Applicants</th>
                    <th className="fs-13">Posted</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.recentJobs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted fs-13 py-4">
                        No jobs posted yet.{' '}
                        <Link to="/recruiter/jobs/new">Post your first job</Link>
                      </td>
                    </tr>
                  ) : (
                    data.recentJobs.map((job: RecentJob) => (
                      <tr key={job.id} style={{ height: 52 }}>
                        <td className="fs-13 fw-medium">
                          <Link
                            to={`/recruiter/jobs/${job.id}/edit`}
                            className="text-decoration-none text-dark"
                          >
                            {job.title}
                          </Link>
                        </td>
                        <td>
                          <StatusPill status={job.status} />
                        </td>
                        <td className="text-center">
                          {job.applicantCount > 0 ? (
                            <Link
                              to={`/recruiter/jobs/${job.id}/applicants`}
                              className="badge bg-primary-transparent text-primary fs-12 text-decoration-none"
                            >
                              {job.applicantCount}
                            </Link>
                          ) : (
                            <span className="fs-12 text-muted">—</span>
                          )}
                        </td>
                        <td className="fs-12 text-muted">{formatDate(job.createdAt)}</td>
                        <td className="text-end">
                          <Link
                            to={`/recruiter/jobs/${job.id}/edit`}
                            className="btn btn-sm btn-light"
                          >
                            <i className="ti ti-edit fs-13" />
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="card custom-card h-100">
            <div className="card-header">
              <div className="card-title fs-14 fw-semibold">
                <i className="ti ti-chart-bar me-2 text-primary" />
                Jobs by Status
              </div>
              <span className="badge bg-light text-muted fs-12">{totalStatusCount} total</span>
            </div>
            <div className="card-body">
              {totalStatusCount === 0 ? (
                <p className="text-muted fs-13 text-center py-3">No jobs yet.</p>
              ) : (
                Object.entries(data.countsByStatus).map(([status, count]) => (
                  <StatusProgressBar
                    key={status}
                    status={status}
                    count={count}
                    total={totalStatusCount}
                  />
                ))
              )}

              <div className="mt-4 pt-3 border-top">
                <Link
                  to="/recruiter/jobs"
                  className="btn btn-primary w-100 btn-sm"
                >
                  <i className="ti ti-list me-1" />
                  Manage All Jobs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
